import { CalendarOccurrence, CalendarScope } from '../../types';

/**
 * 일정 분류색 — 개인 blue · 부서 emerald · 회사 violet. 인트라넷 부서별일정은 부서색 점선 테두리.
 *
 * ⚠ 클래스 이름은 전부 이 정적 맵에 문자열 그대로 둔다. `bg-${color}-100`처럼 조립하면 Tailwind JIT가 스캔하지
 *   못해 빌드·타입 검사가 통과한 채 색만 사라진다.
 * ⚠ 브랜드 레드(brand-*)는 액션·경고·안읽음 전용이라 분류색으로 쓰지 않는다. amber는 관리자 화면 경고색이라 피한다.
 */
export interface ScopeStyle {
  label: string;
  /** 점(월 격자·목록 앞) */
  dot: string;
  /** 칩·막대 바탕 + 글자 */
  chip: string;
  /** 막대 왼쪽 띠·카드 왼쪽 띠 */
  bar: string;
  /** 필터 칩이 켜졌을 때 */
  filterOn: string;
}

export const SCOPE_STYLE: Record<CalendarScope, ScopeStyle> = {
  personal: {
    label: '내 일정',
    dot: 'bg-blue-500',
    chip: 'bg-blue-50 text-blue-800',
    bar: 'border-l-blue-500',
    filterOn: 'bg-blue-50 border-blue-300 text-blue-800',
  },
  dept: {
    label: '부서',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-800',
    bar: 'border-l-emerald-500',
    filterOn: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  },
  company: {
    label: '회사',
    dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-800',
    bar: 'border-l-violet-500',
    filterOn: 'bg-violet-50 border-violet-300 text-violet-800',
  },
};

/** 인트라넷 부서별일정 — 부서색 점선 테두리 + '인트라넷' 배지 */
export const INTRANET_CHIP = 'bg-white text-emerald-800 border border-dashed border-emerald-400';

/** 미응답 초대(참석자) — 점선 */
export const PENDING_CHIP_EXTRA = 'border border-dashed border-blue-300';

export function chipClassOf(o: Pick<CalendarOccurrence, 'source' | 'scope' | 'myRole' | 'myResponse'>): string {
  if (o.source === 'intranet') return INTRANET_CHIP;
  const base = SCOPE_STYLE[o.scope]?.chip || SCOPE_STYLE.personal.chip;
  return o.myRole === 'attendee' && o.myResponse === 'pending' ? `${base} ${PENDING_CHIP_EXTRA}` : base;
}

export function scopeLabelOf(o: Pick<CalendarOccurrence, 'source' | 'scope'>, deptName?: string | null): string {
  if (o.source === 'intranet') return '인트라넷 부서 일정';
  if (o.scope === 'dept') return deptName ? `${deptName} 일정` : '부서 일정';
  if (o.scope === 'company') return '회사 일정';
  return '내 일정';
}

export const CALENDAR_FILTER_KEY = 'calendarFilters';
export type CalendarFilters = Record<CalendarScope, boolean>;
export const DEFAULT_FILTERS: CalendarFilters = { personal: true, dept: true, company: true };

export function readFilters(): CalendarFilters {
  try {
    const raw = localStorage.getItem(CALENDAR_FILTER_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const v = JSON.parse(raw);
    return {
      personal: v.personal !== false,
      dept: v.dept !== false,
      company: v.company !== false,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export function writeFilters(f: CalendarFilters) {
  try { localStorage.setItem(CALENDAR_FILTER_KEY, JSON.stringify(f)); } catch { /* 저장 실패는 무시 */ }
}
